import React from 'react'
import {Heart} from 'lucide-react'
import { motion } from 'framer-motion'

interface Prayer {
  _id: string
  name: string
  message: string
  createdAt: string
  type?: string
}

interface PrayerWallProps {
  prayers: Prayer[]
  loading: boolean
  limit?: number
  showAll?: boolean
}

const PrayerWall: React.FC<PrayerWallProps> = ({ prayers, loading, limit, showAll = false }) => {
  const displayPrayers = limit ? prayers.slice(0, limit) : prayers

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#6b8f71]"></div>
        <p className="ml-4 text-gray-600">Carregando pedidos...</p>
      </div>
    )
  }

  if (displayPrayers.length === 0) {
    return (
      <div className="bg-white rounded-xl p-8 text-center text-gray-500 shadow-md">
        <Heart className="mx-auto text-gray-300 mb-4" size={48} />
        <p>Nenhum pedido público aprovado ainda.</p>
      </div>
    )
  }

  return (
    <div className={showAll ? 'space-y-6' : 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6'}>
      {displayPrayers.map((prayer, index) => (
        <motion.div
          key={prayer._id}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: index * 0.1 }}
          className={`bg-white rounded-xl p-6 shadow-md hover:shadow-lg transition-shadow ${
            showAll ? 'flex items-start gap-4' : ''
          }`}
        >
          <div className={`${showAll ? 'bg-[#6b8f71]/10 p-3 rounded-full flex-shrink-0' : 'flex items-start gap-3 mb-4'}`}>
            <Heart className={`text-[#6b8f71] ${showAll ? '' : 'flex-shrink-0 mt-1'}`} size={24} />
            {!showAll && (
              <div className="flex-1">
                <h3 className="font-semibold text-gray-900 mb-1">{prayer.name}</h3>
                {prayer.type && (
                  <span className="inline-block text-xs font-medium px-2 py-1 bg-[#1e3a8a]/10 text-[#1e3a8a] rounded-full mb-2">
                    {prayer.type}
                  </span>
                )}
              </div>
            )}
          </div>
          
          {showAll && (
            <div className="flex-1">
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-semibold text-[#1e3a8a]">{prayer.name}</h3>
                {prayer.type && (
                  <span className="text-xs font-medium px-3 py-1 bg-[#1e3a8a]/10 text-[#1e3a8a] rounded-full">
                    {prayer.type}
                  </span>
                )}
              </div>
              <p className="text-sm text-gray-500 mb-2">
                {new Date(prayer.createdAt).toLocaleDateString('pt-BR', {
                  day: '2-digit',
                  month: 'long',
                  year: 'numeric'
                })}
              </p>
            </div>
          )}

          {!showAll && (
            <div className="flex items-center justify-between text-sm mt-2">
              <span className="text-gray-500">
                {new Date(prayer.createdAt).toLocaleDateString('pt-BR')}
              </span>
              <span className="text-[#6b8f71] font-semibold flex items-center gap-1">
                <Heart size={14} />
                Em oração
              </span>
            </div>
          )}
        </motion.div>
      ))}
    </div>
  )
}

export default PrayerWall
